package com.evoapps.evoshift.domain.usecase.kiosk

import com.evoapps.evoshift.domain.model.BreakRules
import com.evoapps.evoshift.domain.model.KioskPerson
import com.evoapps.evoshift.domain.model.KioskTile
import com.evoapps.evoshift.domain.model.KioskTileState
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.UnplannedKind
import com.evoapps.evoshift.domain.model.UnplannedStatus
import com.evoapps.evoshift.domain.usecase.shift.ClockPick
import com.evoapps.evoshift.domain.usecase.shift.ClockRules
import kotlinx.datetime.DatePeriod
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.minus

/**
 * The tablet's "Who's clocking in?" grid: the people on shift today (and anyone still working an
 * overnight shift from yesterday), one tile each, sorted by shift start. Each tile's state comes
 * from the same [ClockRules] the phone uses, so the PIN pad offers what the server will accept.
 */
object KioskBoard {
    /** The days whose shifts the tablet follows: yesterday (overnight shifts) and today. */
    fun days(now: LocalDateTime): List<String> = listOf(now.date.minus(DatePeriod(days = 1)).toString(), now.date.toString())

    fun tiles(shifts: List<Shift>, team: List<KioskPerson>, now: LocalDateTime, zone: TimeZone): List<KioskTile> {
        val people = team.associateBy { it.userId }
        val today = now.date.toString()
        // SH-134: a shift someone covers for a colleague is the coverer's tile.
        fun clocker(shift: Shift): String? = shift.unplanned
            ?.takeIf { it.kind == UnplannedKind.COVER && it.status != UnplannedStatus.REJECTED }?.userId ?: shift.userId
        return shifts
            .filter { it.isPublished && clocker(it) != null && clocker(it) in people }
            .groupBy { clocker(it)!! }
            .mapNotNull { (userId, theirs) ->
                val person = people.getValue(userId)
                when (val pick = ClockRules.pick(theirs, now, zone, times)) {
                    is ClockPick.Working -> {
                        // SH-116: on a break, the main button ends it.
                        val onBreak = BreakRules.open(pick.shift.breaks)
                        if (onBreak != null) tile(person, pick.shift, KioskTileState.ON_BREAK, pick.since).copy(breakSince = onBreak.start.hhmm())
                        else tile(person, pick.shift, KioskTileState.WORKING, pick.since)
                    }
                    is ClockPick.CanClockIn -> tile(person, pick.shift, KioskTileState.CAN_CLOCK_IN)
                    is ClockPick.Done -> {
                        // Done, unless another shift of theirs is still to come today.
                        val later = theirs.filter { it.date == today && it.actualTime?.start.isNullOrEmpty() }
                            .minByOrNull { it.plannedTime.start }
                        if (later != null) tile(person, later, KioskTileState.LATER) else tile(person, pick.shift, KioskTileState.DONE, pick.start)
                    }
                    ClockPick.Idle -> theirs.filter { it.date == today && it.actualTime?.start.isNullOrEmpty() }
                        .minByOrNull { it.plannedTime.start }
                        ?.let { tile(person, it, KioskTileState.LATER) }
                }
            }
            .sortedWith(compareBy({ it.shift.plannedTime.start }, { it.person.displayName }))
    }

    /** Everyone else of the team for "Someone else", by name, filtered by [query]. */
    fun others(team: List<KioskPerson>, tiles: List<KioskTile>, query: String): List<KioskPerson> {
        val shown = tiles.map { it.person.userId }.toSet()
        val q = query.trim().lowercase()
        return team.filter { it.userId !in shown && (q.isEmpty() || q in it.displayName.lowercase()) }
            .sortedBy { it.displayName.lowercase() }
    }

    private fun tile(person: KioskPerson, shift: Shift, state: KioskTileState, clockedIn: String? = null) = KioskTile(
        person = person,
        shift = shift,
        state = state,
        plannedStart = shift.plannedTime.start.hhmm(),
        plannedEnd = shift.plannedTime.end.hhmm(),
        clockedIn = clockedIn
    )

    private val times = ClockRules.Times { s: Shift ->
        ClockRules.ShiftTimes(s.plannedTime.start, s.plannedTime.end, s.actualTime?.start, s.actualTime?.end)
    }

    private fun String.hhmm() = substringAfter('T').take(5)
}
